"use client";

import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useGetOrderByIdQuery } from "@/features/api/orderApi";

const OrderStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  /* -------------------------------------------------- */
  /* GET ORDER ID FROM URL                             */
  /* -------------------------------------------------- */
  const searchParams = new URLSearchParams(location.search);

  const orderId =
    searchParams.get("orderId") ||
    searchParams.get("merchantOrderId");

  /* -------------------------------------------------- */
  /* FETCH ORDER (Polling for status change)           */
  /* -------------------------------------------------- */
  const {
    data,
    isLoading,
    isError,
    error,
  } = useGetOrderByIdQuery(orderId, {
    skip: !orderId,
    pollingInterval: 4000,
  });

  const order = data?.data || data;

  const status = useMemo(() => {
    return order?.status?.toUpperCase() || "PENDING";
  }, [order]);

  /* -------------------------------------------------- */
  /* ERROR HANDLING                                    */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (isError) {
      toast.error(
        error?.data?.message || "Failed to fetch order details"
      );
    }
  }, [isError, error]);

  /* -------------------------------------------------- */
  /* AUTO REDIRECT AFTER SUCCESS                       */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (!order) return;

    if (status === "SUCCESS") {
      // Course
      if (order.productType === "course" && order.courseId) {
        setTimeout(() => {
          navigate(`/courses/${order.courseId}/learn`, {
            replace: true,
          });
        }, 2500);
      }

      // Mock test / online product
      if (
        order.productType === "ONLINE_PAYMENT" ||
        order.productType === "mock"
      ) {
        setTimeout(() => {
          navigate("/mock-tests", { replace: true });
        }, 2500);
      }
    }
  }, [status, order, navigate]);

  /* -------------------------------------------------- */
  /* INVALID ORDER ID                                  */
  /* -------------------------------------------------- */
  if (!orderId) {
    return (
      <Centered>
        <MessageCard
          title="Invalid Request"
          color="yellow"
          message="Missing order reference."
          buttonText="Back"
          buttonPath="/"
        />
      </Centered>
    );
  }

  /* -------------------------------------------------- */
  /* LOADING                                           */
  /* -------------------------------------------------- */
  if (isLoading) {
    return (
      <Centered>
        <div className="flex flex-col items-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
          <span className="mt-4 text-lg text-blue-700">
            Checking payment status...
          </span>
        </div>
      </Centered>
    );
  }

  /* -------------------------------------------------- */
  /* ERROR STATE                                       */
  /* -------------------------------------------------- */
  if (isError || !order) {
    return (
      <Centered>
        <MessageCard
          title="Error"
          color="red"
          message="Could not fetch your order."
          buttonText="Back"
          buttonPath="/"
        />
      </Centered>
    );
  }

  /* -------------------------------------------------- */
  /* SUCCESS / PENDING / FAILED                        */
  /* -------------------------------------------------- */
  return (
    <Centered>
      <StatusCard
        status={status}
        order={order}
      />
    </Centered>
  );
};

/* ================================================== */
/* COMPONENTS                                         */
/* ================================================== */

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
    {children}
  </div>
);

const StatusCard = ({ status, order }) => {
  const colorMap = {
    SUCCESS: {
      bg: "bg-green-50",
      text: "text-green-700",
      button: "bg-green-600",
      title: "Payment Successful 🎉",
    },
    PENDING: {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      button: "bg-yellow-600",
      title: "Payment Pending ⏳",
    },
    FAILED: {
      bg: "bg-red-50",
      text: "text-red-700",
      button: "bg-red-600",
      title: "Payment Failed ❌",
    },
  };

  const theme = colorMap[status] || colorMap.PENDING;

  return (
    <div
      className={`w-full max-w-lg p-8 rounded-2xl shadow-lg border ${theme.bg}`}
    >
      <h1
        className={`text-3xl font-bold mb-4 text-center ${theme.text}`}
      >
        {theme.title}
      </h1>

      <p className={`text-md mb-6 text-center ${theme.text}`}>
        {status === "PENDING"
          ? "We are verifying your payment. Please wait..."
          : status === "SUCCESS"
          ? "Your transaction was processed successfully."
          : "Your payment could not be completed."}
      </p>

      <div className={`space-y-2 mb-6 ${theme.text}`}>
        <p>
          <strong>Product:</strong>{" "}
          {order.courseName || "Item"}
        </p>

        <p>
          <strong>Amount:</strong> ₹
          {Number(order.amount || 0).toFixed(2)}
        </p>

        <p>
          <strong>Transaction ID:</strong>{" "}
          {order.transactionId}
        </p>
      </div>

      <div className="flex justify-center">
        {status === "FAILED" ? (
          <Link
            to="/checkout"
            className={`px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition ${theme.button}`}
          >
            Retry Payment
          </Link>
        ) : (
          <Link
            to="/"
            className={`px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition ${theme.button}`}
          >
            Back to Home
          </Link>
        )}
      </div>
    </div>
  );
};

const MessageCard = ({
  title,
  message,
  buttonText,
  buttonPath,
  color,
}) => {
  const colorMap = {
    red: "bg-red-50 text-red-700",
    yellow: "bg-yellow-50 text-yellow-700",
  };

  return (
    <div
      className={`w-full max-w-lg p-8 rounded-2xl shadow-lg border ${colorMap[color]}`}
    >
      <h1 className="text-2xl font-bold mb-4 text-center">
        {title}
      </h1>
      <p className="mb-6 text-center">{message}</p>
      <div className="flex justify-center">
        <Link
          to={buttonPath}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
        >
          {buttonText}
        </Link>
      </div>
    </div>
  );
};

export default OrderStatusPage;